import React, {Component} from 'react';
import PropTypes from "prop-types";
import _ from "lodash";

import SectionCard from "../Sections/SectionCard";
import ControlForm from "../Controls/ControlForm";
import NationalMap from "../Charts/NationalMap/NationalMap";
import LegendView from "../Charts/Legend/LegendView";
import SectionModal from "../Sections/SectionModal";
import ControlExports from "../Controls/ControlExports";
import DetailTable from "../Tables/DetailTable";
import SelectionTable from "../Tables/SelectionTable";

import * as Utils from "../../utilities/utilities";

import {TEXT} from '../../utilities/strings';
import {VARIABLES, DISPLAYS} from "../../utilities/constants";

/**
 * Component to select Components for the states section, and pass them into the SectionCard
 *
 * @example
 * <StatesSection
 *   loading={this.props.loading}
 *   selectedState={this.props.selectedState}
 *   comparisonState={this.props.comparisonState}
 *   selectedCounty={this.props.selectedCounty}
 *   comparisonCounty={this.props.comparisonCounty}
 *   nationalData={this.props.nationalData}
 *   statesData={this.props.statesData}
 *   countiesData={this.props.countiesData}
 *   summaryType={this.props.summaryType}
 *   displayType={this.props.displayType}
 *   selectedCost={this.props.selectedCost}
 *   selectedStateFips={this.props.selectedStateFips}
 *   comparisonStateFips={this.props.comparisonStateFips}
 *   selectedCountyFips={this.props.selectedCountyFips}
 *   comparisonCountyFips={this.props.comparisonCountyFips}
 *   onExportData={this.props.exportStatesData}
 *   onExportImage={this.props.exportStatesImage}
 *   onExportVector={this.props.exportStatesVector}
 *   onSelectedStateChange={this.props.setSelectedStateFips}
 *   onComparisonStateChange={this.props.setComparisonStateFips}
 *   onUpdateSelections={this.props.setFormSelections}
 *  />
 */
export default class StatesSection extends Component {

  static propTypes = {
    loading: PropTypes.bool,
    selectedState: PropTypes.object,
    comparisonState: PropTypes.object,
    selectedCounty: PropTypes.object,
    comparisonCounty: PropTypes.object,
    nationalData: PropTypes.array,
    statesData: PropTypes.array,
    countiesData: PropTypes.array,
    summaryType: PropTypes.string,
    displayType: PropTypes.string,
    selectedCost: PropTypes.string,
    selectedStateFips: PropTypes.number,
    comparisonStateFips: PropTypes.number,
    selectedCountyFips: PropTypes.number,
    comparisonCountyFips: PropTypes.number,
    onExportData: PropTypes.func.isRequired,
    onExportImage: PropTypes.func.isRequired,
    onExportVector: PropTypes.func.isRequired,
    onSelectedStateChange: PropTypes.func.isRequired,
    onComparisonStateChange: PropTypes.func.isRequired,
    onUpdateSelections: PropTypes.func.isRequired
  };

  render() {
    const tableId = 'state-table';
    const sectionType = 'state';

    const showTable = this.props.displayType !== DISPLAYS.CHART;

    const title = Utils.getStateSectionTitle(this.props.selectedCost);
    const footnote = Utils.getSectionFootnote(this.props.selectedCost);

    const mapProperty = Utils.getMapValueProperty(this.props.selectedCost);
    const legendProperty = Utils.getLegendValueProperty(this.props.selectedCost);
    const legendTitle = Utils.getLegendValueTitle(this.props.selectedCost);

    const summary = (
      <div>
        <span>{TEXT.state_description}</span>
        <SectionModal title={TEXT.state_info_title} body={TEXT.state_info_body}/>
      </div>
    );

    const controls = (
      <ControlForm
        formName={sectionType}
        focusIdOnSubmit={tableId}
        showSelectedCost={true}
        showSelectedState={true}
        showComparisonState={true}
        showDisplayType={true}
        countiesData={this.props.countiesData}
        statesData={this.props.statesData}
        displayType={this.props.displayType}
        summaryType={this.props.summaryType}
        selectedCost={this.props.selectedCost}
        selectedStateFips={this.props.selectedStateFips}
        comparisonStateFips={this.props.comparisonStateFips}
        selectedCountyFips={this.props.selectedCountyFips}
        comparisonCountyFips={this.props.comparisonCountyFips}
        onSubmit={this.props.onUpdateSelections}
      />
    );

    const table = (
      <DetailTable
        focusId={tableId}
        caption={footnote}
        rowKey={VARIABLES.StateID}
        data={this.props.statesData}
        loading={this.props.loading}
        columns={Utils.getDetailColumns(this.props.selectedCost)}
        selectedRowId={this.props.selectedStateFips}
        comparisonRowId={this.props.comparisonStateFips}
        onSelect={this.props.onSelectedStateChange}
        onComparison={this.props.onComparisonStateChange}
      />
    )

    const chart = (
      <div>
        <NationalMap
          colorField={mapProperty}
          legendField={legendProperty}
          data={this.props.statesData}
          dict={_.keyBy(this.props.statesData, VARIABLES.StateID)}
          selectedCost={this.props.selectedCost}
          selectedIds={[this.props.selectedStateFips, this.props.comparisonStateFips]}
          onClick={this.props.onSelectedStateChange}
          onAltClick={this.props.onComparisonStateChange}
        />
        <SelectionTable
          caption={footnote}
          type={sectionType}
          nationalData={this.props.nationalData}
          selectedLocation={this.props.selectedState}
          comparisonLocation={this.props.comparisonState}
          columns={Utils.getSelectionColumns(this.props.selectedCost)}
        />
      </div>
    )

    const exports = (
      <ControlExports
        onExportData={this.props.onExportData}
        onExportImage={this.props.onExportImage}
        onExportVector={this.props.onExportVector}
      />
    )

    const legend = (
      <LegendView
        data={this.props.statesData}
        title={legendTitle}
        quantileField={mapProperty}
        valueField={legendProperty}
      />
    )

    /**
     * Using constants above to pass into SectionCard for rendering
     */
    return (
      <SectionCard
        section={sectionType}
        title={title}
        exports={exports}
        summary={summary}
        controls={controls}
        legend={legend}
        content={chart}
        alternate={table}
        footnote={TEXT.footnote_suppression_short}
        showAlternate={showTable}
      />
    );
  }
}
